interface Member {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  role: "member" | "leader" | "moderator";
}

interface Props {
  members: Member[];
  totalCount: number;
}

/**
 * Calm members display — just the count + a tiny avatar stack of leaders/
 * moderators first. No grid of every member (that's social-media energy).
 */
export function MembersPreview({ members, totalCount }: Props) {
  if (totalCount === 0) {
    return (
      <p className="text-sm text-whisper">First members forming.</p>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <div className="flex -space-x-2">
        {members.slice(0, 4).map((m) => (
          <Avatar key={m.id} member={m} />
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted">
        <span className="text-whisper tabular-nums">{totalCount}</span>{" "}
        {totalCount === 1 ? "member" : "members"}
      </div>
    </div>
  );
}

function Avatar({ member }: { member: Member }) {
  const initial = (member.display_name ?? "·")[0]?.toUpperCase() ?? "·";
  const ring =
    member.role === "leader"
      ? "ring-2 ring-gold/50"
      : member.role === "moderator"
        ? "ring-1 ring-gold/30"
        : "ring-1 ring-veil/60";
  return (
    <div
      title={member.display_name ?? "member"}
      className={`size-8 rounded-full bg-veil/50 flex items-center justify-center text-xs font-display text-fog ${ring}`}
    >
      {member.avatar_url ? (
        <img
          src={member.avatar_url}
          alt=""
          className="size-full rounded-full object-cover"
        />
      ) : (
        initial
      )}
    </div>
  );
}
